// Vínculo entre conteúdo (pílula/questão) e minicursos, a partir das etapas dos cursos:
// filtro "Curso" e etiqueta "curso · etapa" usados nas listas de Pílulas e Quiz.
import React, { useEffect, useMemo, useState } from 'react';
import { FiLayers } from 'react-icons/fi';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from '../../config/firebaseConfig';
import './CursosDoConteudo.css';

export const FORA_DE_CURSO = '__fora_de_curso';

export function useCursosDoConteudo(tenantId) {
  const [minicursos, setMinicursos] = useState([]);
  const [etapas, setEtapas] = useState([]);

  useEffect(() => {
    if (!tenantId) return undefined;
    const pararCursos = onSnapshot(
      query(collection(firestore, 'minicursos'), where('tenantId', '==', tenantId)),
      (s) => setMinicursos(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error('Minicursos:', e),
    );
    const pararEtapas = onSnapshot(
      query(collection(firestore, 'etapasMinicurso'), where('tenantId', '==', tenantId)),
      (s) => setEtapas(s.docs.map((d) => d.data())),
      (e) => console.error('Etapas dos minicursos:', e),
    );
    return () => { pararCursos(); pararEtapas(); };
  }, [tenantId]);

  // conteudoId -> [{ minicursoId, nome, ordem, ativo }], ordenado por curso e etapa
  const etapasPorConteudo = useMemo(() => {
    const cursos = Object.fromEntries(minicursos.map((m) => [m.id, m]));
    const mapa = {};
    etapas.forEach((e) => {
      const curso = cursos[e.minicursoId];
      if (!curso) return;
      (mapa[e.conteudoId] = mapa[e.conteudoId] || []).push({
        minicursoId: e.minicursoId, nome: curso.nome, ordem: e.ordem, ativo: curso.status === 'ativo',
      });
    });
    Object.values(mapa).forEach((lista) => lista.sort((a, b) => a.nome.localeCompare(b.nome) || a.ordem - b.ordem));
    return mapa;
  }, [minicursos, etapas]);

  const cursosOrdenados = useMemo(
    () => [...minicursos].sort((a, b) => (b.status === 'ativo') - (a.status === 'ativo') || String(a.nome).localeCompare(String(b.nome))),
    [minicursos],
  );

  return { minicursos: cursosOrdenados, etapasPorConteudo };
}

// Aplica o filtro de curso; com um curso escolhido, ordena pela etapa
export function filtrarPorCurso(itens, cursoSelecionado, etapasPorConteudo) {
  if (!cursoSelecionado) return itens;
  if (cursoSelecionado === FORA_DE_CURSO) return itens.filter((i) => !(etapasPorConteudo[i.id] || []).length);
  const ordem = (i) => (etapasPorConteudo[i.id] || []).find((v) => v.minicursoId === cursoSelecionado)?.ordem;
  return itens
    .filter((i) => ordem(i) !== undefined)
    .sort((a, b) => ordem(a) - ordem(b));
}

export function FiltroCurso({ minicursos, valor, onChange }) {
  return (
    <>
      <label htmlFor="curso-filter">Curso:</label>
      <select id="curso-filter" value={valor} onChange={(e) => onChange(e.target.value)} aria-label="Filtrar por minicurso">
        <option value="">Todos os cursos</option>
        {minicursos.map((m) => (
          <option key={m.id} value={m.id}>{m.nome}{m.status === 'ativo' ? '' : ' (inativo)'}</option>
        ))}
        <option value={FORA_DE_CURSO}>Fora de minicurso (avulsas)</option>
      </select>
    </>
  );
}

// Etiquetas "curso · etapa N" abaixo do título do item
export function CursosDoItem({ vinculos, cursoSelecionado }) {
  const lista = (vinculos || []).filter((v) => !cursoSelecionado || cursoSelecionado === FORA_DE_CURSO || v.minicursoId === cursoSelecionado);
  if (!lista.length) return null;
  return (
    <span className="cc-cursos">
      {lista.map((v) => (
        <span key={`${v.minicursoId}-${v.ordem}`} className={`cc-curso${v.ativo ? '' : ' cc-curso--inativo'}`}>
          <FiLayers aria-hidden="true" />
          {cursoSelecionado ? `Etapa ${v.ordem}` : `${v.nome} · etapa ${v.ordem}`}
        </span>
      ))}
    </span>
  );
}
